// /opt/isp_erp/frontend/src/components/warehouse/materiales/StockTecnicoPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Alert,
  CircularProgress, TablePagination, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
  Checkbox, ListItemText, OutlinedInput, Divider, IconButton, Tooltip
} from '@mui/material';
import { Refresh, SwapHoriz, PictureAsPdf, ExpandMore, ExpandLess, AssignmentReturn, Warning } from '@mui/icons-material';
import { warehouseAPI } from '../../../api/warehouse.api';
import { CampoBusqueda, SelectProveedor, colorTipo, detalleError, fmtNum, fechaLocal, useDebouncedValue } from './utils';

const StockTecnicoPanel = ({ tecnicos, proveedores, onVerMovimientos, ocultarPdf = false }) => {
  // ---------- Reporte PDF de asignación (con firmas) ----------
  const [tecnicosPDF, setTecnicosPDF] = useState([]);
  const [fechaDesde, setFechaDesde] = useState(fechaLocal(new Date()));
  const [fechaHasta, setFechaHasta] = useState(fechaLocal(new Date()));
  const [firmaBodegaNombre, setFirmaBodegaNombre] = useState('');
  const [firmasTecnicos, setFirmasTecnicos] = useState({});
  const [firmaAbierta, setFirmaAbierta] = useState(false);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const [errorPdf, setErrorPdf] = useState('');
  const [mensajePdf, setMensajePdf] = useState('');

  const nombreDe = (id) => {
    const t = (tecnicos || []).find((x) => x.id === id);
    return t ? (t.username || t.nombre || t.email) : String(id);
  };

  const exportarPDF = async () => {
    setErrorPdf('');
    if (tecnicosPDF.length === 0) {
      setErrorPdf('Seleccione al menos un técnico para el reporte');
      return;
    }
    if (!firmaBodegaNombre.trim()) {
      setErrorPdf('Ingrese el nombre del personal de bodega que firma');
      return;
    }
    const sinFirma = tecnicosPDF.filter((id) => !firmasTecnicos[id]?.trim());
    if (sinFirma.length > 0) {
      setErrorPdf(`Ingrese el nombre de los técnicos que firman: ${sinFirma.map(nombreDe).join(', ')}`);
      return;
    }
    setGenerandoPdf(true);
    try {
      const params = {
        tecnico_ids: tecnicosPDF.join(','),
        firma_bodega_nombre: firmaBodegaNombre.replace(/,/g, ' '),
        firma_tecnico_nombres: tecnicosPDF.map((id) => firmasTecnicos[id].replace(/,/g, ' ')).join(',')
      };
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const r = await warehouseAPI.descargarAsignacionPDF(params);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      let sufijo = '';
      if (fechaDesde && fechaHasta) sufijo = `_${fechaDesde}_a_${fechaHasta}`;
      else if (fechaDesde) sufijo = `_desde_${fechaDesde}`;
      else if (fechaHasta) sufijo = `_hasta_${fechaHasta}`;
      link.setAttribute('download', `asignacion_${tecnicosPDF.map(nombreDe).join('_')}${sufijo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setFirmaBodegaNombre('');
      setFirmasTecnicos({});
      setFirmaAbierta(false);
      setMensajePdf(`PDF generado con ${tecnicosPDF.length} técnico(s) y espacio de firmas`);
      setTimeout(() => setMensajePdf(''), 6000);
    } catch (err) {
      setErrorPdf('Error al generar el PDF');
    } finally {
      setGenerandoPdf(false);
    }
  };

  // ---------- Stock individual por técnico ----------
  const [tecnicoStock, setTecnicoStock] = useState('');
  const [texto, setTexto] = useState('');
  const busqueda = useDebouncedValue(texto.trim(), 400);
  const [proveedorId, setProveedorId] = useState('');
  const [soloBajo, setSoloBajo] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  useEffect(() => {
    setPage(0);
  }, [busqueda]);

  const cargar = useCallback(async () => {
    if (!tecnicoStock) {
      setData({ items: [], total: 0 });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = { page: page + 1, page_size: rowsPerPage, solo_bajo_minimo: soloBajo };
      if (busqueda) params.q = busqueda;
      if (proveedorId !== '') params.proveedor_id = proveedorId;
      const r = await warehouseAPI.stockTecnicoPaginado(tecnicoStock, params);
      setData(r.data);
    } catch (err) {
      setData({ items: [], total: 0 });
      setError(detalleError(err, 'Error al cargar el stock del técnico'));
    } finally {
      setLoading(false);
    }
  }, [tecnicoStock, busqueda, proveedorId, soloBajo, page, rowsPerPage]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // ---------- Devolver material a bodega ----------
  const [devolviendo, setDevolviendo] = useState(null);
  const [cantidad, setCantidad] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [errorDev, setErrorDev] = useState('');
  const [enviando, setEnviando] = useState(false);

  const abrirDevolver = (item) => {
    setDevolviendo(item);
    setCantidad('');
    setObservaciones('');
    setErrorDev('');
  };

  const confirmarDevolver = async () => {
    const cant = parseFloat(cantidad);
    if (!(cant > 0)) {
      setErrorDev('La cantidad debe ser mayor a 0');
      return;
    }
    if (cant > devolviendo.cantidad) {
      setErrorDev(`El técnico solo tiene ${fmtNum(devolviendo.cantidad)} ${devolviendo.unidad}`);
      return;
    }
    setEnviando(true);
    setErrorDev('');
    try {
      const params = { material_id: devolviendo.material_id, tecnico_id: tecnicoStock, cantidad: cant };
      if (observaciones.trim()) params.observaciones = observaciones.trim();
      await warehouseAPI.devolverMaterialBodega(params);
      setDevolviendo(null);
      setMensaje('Material devuelto a bodega correctamente');
      setTimeout(() => setMensaje(''), 5000);
      cargar();
    } catch (err) {
      setErrorDev(detalleError(err, 'Error al devolver el material'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Card>
      <CardContent>
        {/* ---------- Reporte PDF ---------- */}
        {(
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Reporte PDF de asignación a técnicos</Typography>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Selecciona uno o varios técnicos para generar el reporte con espacio para firmas. Incluye solo lo entregado en el rango de fechas (por defecto, hoy); borra las fechas para ver todo el historial.
          </Typography>
          {mensajePdf && <Alert severity="success" sx={{ my: 1 }}>{mensajePdf}</Alert>}
          {errorPdf && <Alert severity="error" sx={{ my: 1 }} onClose={() => setErrorPdf('')}>{errorPdf}</Alert>}
          <Grid container spacing={2} alignItems="center" sx={{ mt: 0 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Técnicos para el PDF</InputLabel>
                <Select
                  multiple
                  value={tecnicosPDF}
                  onChange={(e) => {
                    setTecnicosPDF(e.target.value);
                    setFirmaAbierta(e.target.value.length > 0);
                    if (e.target.value.length === 0) {
                      setFirmaBodegaNombre('');
                      setFirmasTecnicos({});
                    }
                  }}
                  input={<OutlinedInput label="Técnicos para el PDF" />}
                  renderValue={(sel) => sel.map(nombreDe).join(', ')}
                >
                  {(tecnicos || []).map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      <Checkbox checked={tecnicosPDF.indexOf(t.id) > -1} />
                      <ListItemText primary={t.username || t.nombre || t.email} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" type="date" label="Fecha desde" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" type="date" label="Fecha hasta" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdf />}
                endIcon={firmaAbierta ? <ExpandLess /> : <ExpandMore />}
                disabled={tecnicosPDF.length === 0}
                onClick={() => setFirmaAbierta(!firmaAbierta)}
              >
                {firmaAbierta ? 'Ocultar firmas' : `Configurar firmas (${tecnicosPDF.length} técnico(s))`}
              </Button>
            </Grid>
          </Grid>

          {firmaAbierta && tecnicosPDF.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" color="success.main" gutterBottom>
                Datos para las firmas
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" fontWeight="bold" sx={{ pt: 1 }}>Personal de bodega</Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField fullWidth size="small" placeholder="Ej: Juan Pérez - Jefe de Bodega" value={firmaBodegaNombre} onChange={(e) => setFirmaBodegaNombre(e.target.value)} />
                </Grid>
                {tecnicosPDF.map((id) => (
                  <React.Fragment key={id}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" fontWeight="bold" sx={{ pt: 1 }}>Técnico: {nombreDe(id)}</Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Nombre completo del técnico"
                        value={firmasTecnicos[id] || ''}
                        onChange={(e) => setFirmasTecnicos({ ...firmasTecnicos, [id]: e.target.value })}
                      />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Button
                fullWidth
                variant="contained"
                startIcon={generandoPdf ? <CircularProgress size={18} /> : <PictureAsPdf />}
                onClick={exportarPDF}
                disabled={generandoPdf || !firmaBodegaNombre.trim() || tecnicosPDF.some((id) => !firmasTecnicos[id]?.trim())}
              >
                Generar PDF con firmas (horizontal)
              </Button>
            </Box>
          )}
        </Paper>

                )}
        {/* ---------- Stock individual ---------- */}
        <Typography variant="h6" gutterBottom>Stock por técnico</Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Técnico</InputLabel>
                <Select label="Técnico" value={tecnicoStock} onChange={(e) => { setTecnicoStock(e.target.value); setPage(0); }}>
                  <MenuItem value="">Seleccione un técnico</MenuItem>
                  {(tecnicos || []).map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.username || t.nombre || t.email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <CampoBusqueda value={texto} onChange={setTexto} label="Buscar código o nombre" />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <SelectProveedor value={proveedorId} proveedores={proveedores} onChange={(e) => { setProveedorId(e.target.value); setPage(0); }} />
            </Grid>
            <Grid item xs={6} md={1.75}>
              <Button fullWidth size="small" variant="outlined" startIcon={<Refresh />} onClick={cargar} disabled={!tecnicoStock}>
                Actualizar
              </Button>
            </Grid>
            <Grid item xs={12} md={1.75}>
              <Button fullWidth size="small" variant="outlined" startIcon={<SwapHoriz />} disabled={!tecnicoStock} onClick={() => onVerMovimientos(tecnicoStock)}>
                Movimientos
              </Button>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={soloBajo} onChange={(e) => { setSoloBajo(e.target.checked); setPage(0); }} />}
                label="Mostrar solo los que están bajo el mínimo"
              />
            </Grid>
          </Grid>
        </Paper>

        {mensaje && <Alert severity="success" sx={{ mb: 2 }}>{mensaje}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {!tecnicoStock ? (
          <Alert severity="info">Selecciona un técnico para ver su stock de materiales</Alert>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }}>Material</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }}>Tipo</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>Cantidad</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>Mínimo</TableCell>
                    <TableCell align="center" sx={{ bgcolor: '#f5f5f5' }}>Estado</TableCell>
                    <TableCell align="center" sx={{ bgcolor: '#f5f5f5' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                  ) : data.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No hay materiales asignados a este técnico con esos filtros</TableCell>
                    </TableRow>
                  ) : (
                    data.items.map((s) => (
                      <TableRow key={s.material_id} hover sx={{ bgcolor: s.bajo_minimo ? '#fff8e1' : 'inherit' }}>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Typography variant="body2" fontWeight="bold">{s.nombre}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {s.codigo} · {s.proveedor || 'Sin proveedor'}
                          </Typography>
                        </TableCell>
                        <TableCell><Chip size="small" label={s.tipo} color={colorTipo(s.tipo)} /></TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color={s.bajo_minimo ? 'warning.main' : 'inherit'}>
                            {fmtNum(s.cantidad)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{s.unidad}</Typography>
                        </TableCell>
                        <TableCell align="right">{fmtNum(s.stock_minimo)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            color={s.bajo_minimo ? 'warning' : 'success'}
                            icon={s.bajo_minimo ? <Warning /> : undefined}
                            label={s.bajo_minimo ? 'Stock bajo' : 'Disponible'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={ocultarPdf ? "Recibir en la sucursal" : "Devolver a bodega"}>
                            <IconButton size="small" color="warning" onClick={() => abrirDevolver(s)}>
                              <AssignmentReturn fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={data.total || 0}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              onPageChange={(e, nueva) => setPage(nueva)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </>
        )}
      </CardContent>

      <Dialog open={!!devolviendo} onClose={() => !enviando && setDevolviendo(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{ocultarPdf ? 'Recibir material en la sucursal' : 'Devolver material a bodega'}</DialogTitle>
        <DialogContent dividers>
          {errorDev && <Alert severity="error" sx={{ mb: 2 }}>{errorDev}</Alert>}
          {devolviendo && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>{devolviendo.nombre}</strong> ({devolviendo.codigo}) · {devolviendo.proveedor || 'Sin proveedor'}
                <br />
                El técnico tiene <strong>{fmtNum(devolviendo.cantidad)} {devolviendo.unidad}</strong>.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={`Cantidad a devolver (máx. ${fmtNum(devolviendo.cantidad)})`}
                    value={cantidad}
                    onChange={(e) => setCantidad(e.target.value)}
                    inputProps={{ min: 0, step: 'any', max: devolviendo.cantidad }}
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" multiline rows={2} label="Observaciones" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDevolviendo(null)} disabled={enviando}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={confirmarDevolver} disabled={enviando}>
            {enviando ? 'Devolviendo...' : 'Devolver'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default StockTecnicoPanel;
